import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUploadCloud, FiTrash2, FiDownload, FiCheck, FiMoon, FiSun, FiLoader, FiFile, FiFileText, FiClock, FiList } from 'react-icons/fi';
import { AiOutlineFile, AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileImage } from 'react-icons/ai';

import axios from 'axios';
import { initializeApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { showToast } from '../utils/toast';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import ConversionModal from './ConversionModal';
import { getOrCreateUserId } from '../utils/uuid';
import Button from './Button';
import FileStatusIndicator from './FileStatusIndicator';
import ConversionHistory from './ConversionHistory';
import SlideOutPanel from './SlideOutPanel';
import { useConversion } from '../context/ConversionContext';
import { useDarkMode } from '../context/DarkModeContext';
import { log, error, warn } from '../utils/logger';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

// Add these utility functions near the top of the file
const sanitizeFileName = (fileName) => {
  // Split filename into name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  const name = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : '';
  
  // Sanitize only the name part, keep the extension intact
  const sanitizedName = name.replace(/[^a-zA-Z0-9]/g, '_');
  return sanitizedName + extension;
};

const generateProcessingFileName = (userId, conversionType, originalFileName) => {
  const timestamp = Date.now();
  const lastDotIndex = originalFileName.lastIndexOf('.');
  const extension = lastDotIndex !== -1 ? originalFileName.slice(lastDotIndex) : '';
  const nameWithoutExt = lastDotIndex !== -1 ? originalFileName.slice(0, lastDotIndex) : originalFileName;
  const sanitizedName = sanitizeFileName(nameWithoutExt);
  
  return `${userId}_${conversionType}_${timestamp}_${sanitizedName}${extension}`;
};

// Utility functions for base64 conversion
const arrayBufferToBase64 = (buffer) => {
  const binary = String.fromCharCode.apply(null, new Uint8Array(buffer));
  return window.btoa(binary);
};

const base64ToArrayBuffer = (base64) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// First, let's create a styled button component for consistency
const ActionButton = ({ icon, children, onClick, variant = "default" }) => {
  const { selectedConversion, currentTheme } = useConversion();
  const { darkMode, toggleDarkMode } = useDarkMode();
  
  // Get theme color for hover effect
  const getThemeColor = () => {
    if (selectedConversion === 'default') {
      return 'text-primary-500';
    }
    return currentTheme?.dragActive?.text || 'text-primary-500';
  };

  const baseClasses = "group flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium";
  
  const variants = {
    default: `
      ${darkMode
        ? 'bg-gray-900/90 border border-gray-700 text-neutral-100 shadow-sm hover:bg-gray-800 hover:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none'
        : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'}
    `,
    ghost: `
      ${darkMode
        ? 'bg-transparent text-neutral-400 hover:text-primary-400 hover:bg-gray-800/60 focus:ring-2 focus:ring-primary-500 focus:outline-none'
        : 'bg-transparent hover:bg-gray-100/50 text-gray-600'}
    `
  };

  // Wrap icon with animated container
  const IconWrapper = ({ children }) => (
    <span className={`
      inline-flex transition-all duration-200 transform
      group-hover:scale-110 group-hover:rotate-[-10deg]
      group-hover:${getThemeColor()}
      ${darkMode ? 'text-primary-400' : ''}
    `}>
      {children}
    </span>
  );

  // Wrap text with animated container
  const TextWrapper = ({ children }) => (
    <span className={`
      transition-colors duration-200
      group-hover:${getThemeColor()}
    `}>
      {children}
    </span>
  );

  return (
    <button
      onClick={onClick}
      className={`
        ${baseClasses} 
        ${variants[variant]}
        hover:shadow-sm
        active:scale-95
        transform transition-all duration-200
      `}
    >
      <IconWrapper>
        {icon}
      </IconWrapper>
      <TextWrapper>
        {children}
      </TextWrapper>
    </button>
  );
};

const FileUpload = () => {
  const { currentTheme, selectedConversion } = useConversion();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [conversionInProgress, setConversionInProgress] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileStatuses, setFileStatuses] = useState({});
  const [currentFile, setCurrentFile] = useState(null);
  const [conversionProgress, setConversionProgress] = useState({});
  const [overallProgress, setOverallProgress] = useState(0);
  const [isBatchConverting, setIsBatchConverting] = useState(false);

  const [publicKey, setPublicKey] = useState(null);
  const [aesKeys, setAesKeys] = useState({}); // Store AES keys per file

  
  
  
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const currentUserId = getOrCreateUserId();
    setUserId(currentUserId);
    const storedHistory = JSON.parse(localStorage.getItem(`conversionHistory_${currentUserId}`)) || [];
    setConversionHistory(storedHistory);
  }, []);

  useEffect(() => {
    const isDarkMode = localStorage.getItem('darkMode') === 'true';
    // setDarkMode(isDarkMode); // This line is removed as per the edit hint
  }, []);

  const clearConversionHistory = () => {
    setConversionHistory([]);
    localStorage.removeItem(`conversionHistory_${userId}`);
    showToast.success('Conversion history cleared.');
  };

  // const toggleDarkMode = () => { // This line is removed as per the edit hint
  //   const newDarkMode = !darkMode;
  //   setDarkMode(newDarkMode);
  //   localStorage.setItem('darkMode', newDarkMode);
  // };




  const conversionOptions = useMemo(() => ({
    document: [
      { label: 'PDF to Word', value: 'pdfToWord' },
      { label: 'Word to PDF', value: 'wordToPdf' },
      { label: 'Excel to PDF', value: 'excelToPdf' },
      { label: 'PDF to Excel', value: 'pdfToExcel' },
      { label: 'PowerPoint to PDF', value: 'pptToPdf' },
      { label: 'PDF to PowerPoint', value: 'pdfToPpt' },
      { label: 'Text to PDF', value: 'txtToPdf' },
      { label: 'PDF to Text', value: 'pdfToTxt' },
      { label: 'Markdown to PDF', value: 'mdToPdf' },
      { label: 'PDF to Markdown', value: 'pdfToMd' },
    ],
    image: [
      { label: 'JPEG to PNG', value: 'jpegToPng' },
      { label: 'PNG to JPEG', value: 'pngToJpeg' },
      { label: 'GIF to PNG', value: 'gifToPng' },
      { label: 'PNG to GIF', value: 'pngToGif' },
      { label: 'TIFF to JPEG', value: 'tiffToJpeg' },
      { label: 'JPEG to TIFF', value: 'jpegToTiff' },
      { label: 'SVG to PNG', value: 'svgToPng' },
      { label: 'PNG to SVG', value: 'pngToSvg' },
      { label: 'WEBP to PNG', value: 'webpToPng' },
      { label: 'BMP to JPEG', value: 'bmpToJpeg' },
    ],
    audioVideo: [
      { label: 'MP4 to MP3', value: 'mp4ToMp3' },
      { label: 'MP3 to MP4', value: 'mp3ToMp4' },
      { label: 'AVI to MP4', value: 'aviToMp4' },
      { label: 'MP4 to GIF', value: 'mp4ToGif' },
    ],
    archive: [
      { label: 'ZIP to RAR', value: 'zipToRar' },
      { label: 'RAR to ZIP', value: 'rarToZip' },
      { label: '7Z to ZIP', value: '7zToZip' },
      { label: 'ZIP to 7Z', value: 'zipTo7z' },
      { label: 'TAR to ZIP', value: 'tarToZip' },
      { label: 'ZIP to TAR', value: 'zipToTar' },
      { label: 'TAR.GZ to ZIP', value: 'tarGzToZip' },
      { label: 'ZIP to TAR.GZ', value: 'zipToTarGz' }
    ],
    ebook: [
      { label: 'EPUB to PDF', value: 'epubToPdf' },
      { label: 'PDF to EPUB', value: 'pdfToEpub' },
      { label: 'MOBI to EPUB', value: 'mobiToEpub' },
      { label: 'EPUB to MOBI', value: 'epubToMobi' },
    ],
    cadDesign: [
      { label: 'DWG to PDF', value: 'dwgToPdf' },
      { label: 'PDF to DWG', value: 'pdfToDwg' },
      { label: 'SVG to DXF', value: 'svgToDxf' },
      { label: 'DXF to SVG', value: 'dxfToSvg' },
    ],
    data: [
      { label: 'CSV to JSON', value: 'csvToJson' },
      { label: 'JSON to CSV', value: 'jsonToCsv' },
      { label: 'CSV to Excel', value: 'csvToExcel' },
      { label: 'Excel to CSV', value: 'excelToCsv' },
    ],
    other: [
      { label: 'HTML to PDF', value: 'htmlToPdf' },
      { label: 'LaTeX to PDF', value: 'latexToPdf' },
      { label: 'Markdown to PDF', value: 'markdownToPdf' },
      { label: 'Markdown to HTML', value: 'mdToHtml' },
    ],
  }), []);

  const categoryPriority = ['document', 'ebook', 'image', 'audioVideo', 'archive', 'cadDesign', 'data', 'other'];

  const getCategory = (fileType) => {
    const categories = [];

    // Document types
    if (fileType.includes('word') || fileType.includes('docx') || fileType.includes('doc') ||
      fileType.includes('excel') || fileType.includes('xlsx') || fileType.includes('xls') ||
      fileType.includes('powerpoint') || fileType.includes('ppt') || fileType.includes('txt') ||
      fileType.includes('text') || fileType.includes('markdown') || fileType.includes('md')) {
      categories.push('document');
    }

    // Ebook types
    if (fileType.includes('epub') || fileType.includes('mobi')) {
      categories.push('ebook');
    }

    // PDF can be both document and ebook, but we'll prioritize based on conversion type later
    if (fileType.includes('pdf')) {
      categories.push('document', 'ebook');
    }

    // Image types
    if (fileType.includes('image') || fileType.includes('jpeg') || fileType.includes('jpg') ||
      fileType.includes('png') || fileType.includes('gif') || fileType.includes('tiff') ||
      fileType.includes('svg') || fileType.includes('webp') || fileType.includes('bmp')) {
      categories.push('image');
    }

    // Audio/Video types
    if (fileType.includes('audio') || fileType.includes('video') || fileType.includes('mp4') ||
      fileType.includes('mp3') || fileType.includes('avi') || fileType.includes('mov') ||
      fileType.includes('wav')) {
      categories.push('audioVideo');
    }

    // Archive types
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('7z') || fileType.includes('tar') || fileType.includes('gz')) {
      categories.push('archive');
    }

    // CAD types
    if (fileType.includes('dwg') || fileType.includes('dxf')) {
      categories.push('cadDesign');
    }

    // Data types
    if (fileType.includes('csv') || fileType.includes('json')) {
      categories.push('data');
    }

    // Other types
    if (fileType.includes('html') || fileType.includes('tex')) {
      categories.push('other');
    }

    if (categories.length === 0) {
      categories.push('other');
    }

    return categories;
  };

  const getConversionOptions = (fileName) => {
    const fileType = fileName.split('.').pop().toLowerCase();
    const categories = getCategory(fileType);
    const options = categories.flatMap(category => conversionOptions[category] || []);
    return options;
  };

  const getPrimaryCategory = (fileName, conversionType) => {
    const fileType = fileName.split('.').pop().toLowerCase();
    const categories = getCategory(fileType);

    // Explicitly check for ebook-related conversions
    const ebookConversions = ['pdfToEpub', 'epubToPdf', 'mobiToEpub', 'epubToMobi'];
    if (ebookConversions.includes(conversionType)) {
      return 'ebook';
    }

    // For PDF files, default to 'document' unless it's an ebook conversion
    if (fileType === 'pdf') {
      return 'document';
    }

    // For other file types, use the priority order
    return categories.sort((a, b) => categoryPriority.indexOf(a) - categoryPriority.indexOf(b))[0];
  };

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prevFiles) => [
      ...prevFiles,
      ...acceptedFiles.map(file => ({
        file,
        progress: 0,
        converted: false,
        conversionType: '',
        id: Date.now() + Math.random(),
        categories: getCategory(file.type)
      }))
    ]);
    setFileStatuses(prev => ({
      ...prev,
      ...acceptedFiles.reduce((acc, file) => {
        acc[Date.now() + Math.random()] = 'Uploaded';
        return acc;
      }, {})
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });


  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <AiOutlineFilePdf className="text-red-500 text-2xl" />;
      case 'doc':
      case 'docx':
        return <AiOutlineFileWord className="text-blue-500 text-2xl" />;
      case 'xls':
      case 'xlsx':
        return <AiOutlineFileExcel className="text-green-500 text-2xl" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return <AiOutlineFileImage className="text-purple-500 text-2xl" />;
      case 'tar':
      case 'tgz':
      case 'gz':
        return <AiOutlineFile className="text-yellow-500 text-2xl" />;
      default:
        return <AiOutlineFile className="text-gray-500 text-2xl" />;
    }
  };
  const handleFileConversionChange = (id, conversionType) => {
    setFiles(files.map(file =>
      file.id === id ? { ...file, conversionType } : file
    ));
  };

  const handleUpload = async (fileObj) => {
    try {
      log('=== Starting File Upload Process ===');
      
      // Check if public key is available
      if (!publicKey) {
      error('Public key not available');
        showToast.error(
          'Encryption Key Not Available',
          'Please refresh the page and try again. If the problem persists, contact support.'
        );
        return;
      }
      
      const userId = getOrCreateUserId();
      setConversionProgress(prev => ({ ...prev, [fileObj.id]: 0 }));
      setFileStatuses(prev => ({ ...prev, [fileObj.id]: 'Converting' }));
      setConversionInProgress(prev => ({ ...prev, [fileObj.id]: true }));

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setConversionProgress(prev => {
          const newProgress = Math.min((prev[fileObj.id] || 0) + 10, 90);
          return { ...prev, [fileObj.id]: newProgress };
        });
      }, 500);

      log(`1. Starting upload for file: ${fileObj.file.name} (userId: ${userId})`);
      log('2. Requesting signed URL for upload...');

      const contentType = fileObj.file.type || 'application/octet-stream';

      // Store original filename in memory
      const originalFileName = fileObj.file.name;
      
      // Generate a processing filename
      const processingFileName = generateProcessingFileName(userId, fileObj.conversionType, originalFileName);
      
      // Modify the request to use the processing filename
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/getSignedUrl`, {
        fileName: processingFileName, // Use processing filename
        userId,
        action: 'write',
        contentType,
        conversionType: fileObj.conversionType,
        originalFileName: originalFileName // Add original filename to metadata
      });

      const { signedUrl, publicUrl, token, contentType: returnedContentType } = response.data;
      log('3. Received signed URL and token:');
      log('- Signed URL:', signedUrl);
      log('- Content-Type:', returnedContentType);
      log('- Token:', token);

      // Step 1: Generate and encrypt AES key
      log('4. Generating and encrypting AES key...');
      const { aesKey, encryptedAesKey } = await generateAndEncryptAesKey();
      
      // Store AES key in state for immediate use
      setAesKeys(prev => ({ ...prev, [fileObj.id]: aesKey }));
      
      // Store AES key in localStorage (we'll store it as a serialized array buffer)
      const aesKeyBuffer = await window.crypto.subtle.exportKey('raw', aesKey);
      const aesKeyBase64 = arrayBufferToBase64(aesKeyBuffer);
      localStorage.setItem(`aesKey_${fileObj.id}`, aesKeyBase64);
      
      log('AES key generated and encrypted successfully');
      
      // Step 2: Encrypt the file using AES-CBC
      log('5. Starting file encryption...');
      const iv = window.crypto.getRandomValues(new Uint8Array(16));
      const fileArrayBuffer = await fileObj.file.arrayBuffer();
      const encryptedContent = await window.crypto.subtle.encrypt(
        {
          name: 'AES-CBC',
          iv: iv,
        },
        aesKey,
        fileArrayBuffer
      );

      log('Original file size:', fileArrayBuffer.byteLength);
      log('Encrypted content size:', encryptedContent.byteLength);
      
      // Prepend IV to the encrypted content
      const combinedBuffer = new Uint8Array(iv.byteLength + encryptedContent.byteLength);
      combinedBuffer.set(iv, 0);
      combinedBuffer.set(new Uint8Array(encryptedContent), iv.byteLength);
      
      // Create a new Blob for the encrypted file
      const encryptedBlob = new Blob([combinedBuffer], { type: fileObj.file.type });
      log('Combined encrypted file size:', encryptedBlob.size);
      
      // Prepare FormData
      const formData = new FormData();
      formData.append('file', encryptedBlob, fileObj.file.name);
      formData.append('encryptedAesKey', encryptedAesKey);
      formData.append('conversionType', fileObj.conversionType);

      log('6. File encryption completed');

      // Upload to GCS using the signed URL
      log('7. Uploading encrypted file to storage...');
      await axios.put(signedUrl, encryptedBlob, {
        headers: {
          'Content-Type': returnedContentType,
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });

      log('8. File successfully uploaded to storage:', publicUrl);

      // Proceed with the conversion process using the public URL
      log('9. Starting file conversion process...');
      const primaryCategory = getPrimaryCategory(fileObj.file.name, fileObj.conversionType);
      const conversionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversion/${primaryCategory}`,
        {
          fileUrl: publicUrl,
          fileName: processingFileName,
          originalFileName: originalFileName,
          conversionType: fileObj.conversionType,
          encryptedAesKey: encryptedAesKey
        },
        {
          headers: {
            'Content-Type': 'application/json', // Changed from multipart/form-data
            'Authorization': `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setConversionProgress(prev => ({ ...prev, [fileObj.id]: progress }));
          },
        }
      );

      clearInterval(progressInterval);
      setConversionProgress(prev => ({ ...prev, [fileObj.id]: 100 }));
      setFileStatuses(prev => ({ ...prev, [fileObj.id]: 'Converted' }));

      log('10. Conversion completed successfully:', conversionResponse.data);

      // Update files state with both filenames
      setFiles(prevFiles =>
        prevFiles.map(file =>
          file.id === fileObj.id ? {
            ...file,
            converted: true,
            convertedUrl: conversionResponse.data.url,
            convertedFileName: conversionResponse.data.filename,
            originalFileName: originalFileName, // Store original filename
            token: token
          } : file
        )
      );

      log('11. File processing summary:', {
        originalName: fileObj.file.name,
        convertedName: conversionResponse.data.filename,
        convertedUrl: conversionResponse.data.url,
        token: token
      });

      const newHistoryItem = {
        fileName: fileObj.file.name,
        conversionType: fileObj.conversionType,
        timestamp: new Date().toLocaleString(),
        token: token
      };
      setConversionHistory(prev => {
        const updatedHistory = [...prev, newHistoryItem];
        localStorage.setItem(`conversionHistory_${userId}`, JSON.stringify(updatedHistory));
        return updatedHistory;
      });
      log('12. Conversion history updated');
      showToast.success(
        `${fileObj.file.name} converted successfully!`,
        'Your file is ready for download.'
      );
    } catch (errorObj) {
      error('=== Error in Upload Process ===', errorObj);

      let errorMessage = 'An unexpected error occurred';
      let errorDetails = '';

      if (errorObj.response) {
        // Handle specific API errors
        errorMessage = errorObj.response.data.message || 'Server error occurred';
        errorDetails = `Status: ${errorObj.response.status}. Please try again or contact support if the problem persists.`;
      } else if (errorObj.request) {
        errorMessage = 'Network Error';
        errorDetails = 'Unable to reach the server. Please check your internet connection.';
      } else {
        errorMessage = errorObj.message;
        errorDetails = 'There was an error processing your request.';
      }

      showToast.error(errorMessage, errorDetails);
    } finally {
      setConversionInProgress(prev => ({ ...prev, [fileObj.id]: false }));
      log('=== Upload Process Completed ===');
    }
  };

  const [downloadingFiles, setDownloadingFiles] = useState({});

  const handleDownload = async (url, fileName, token, fileId) => {
    // Set downloading state immediately for instant feedback
    setDownloadingFiles(prev => ({ ...prev, [fileId]: true }));
    
    try {
      log('Starting download process:', { url, fileName, token, fileId });
      
      // Step 1: Verify the token with the backend
      log('Sending verification request for token:', token);
      const verificationResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversion/verifyToken`,
        { token },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      log('Token verification response:', verificationResponse.data);

      if (verificationResponse.data.valid) {
        log('Token verified successfully. Proceeding with download.');

        // Step 2: Download the encrypted file
        log('Starting file download');
        const response = await axios.get(url, {
          responseType: 'arraybuffer',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        log('Encrypted file downloaded.');

        // Step 3: Try to get AES key from state first, then localStorage
        let aesKey = aesKeys[fileId];
        
        if (!aesKey) {
          log('AES key not found in state, checking localStorage...');
          const storedAesKeyBase64 = localStorage.getItem(`aesKey_${fileId}`);
          
          if (!storedAesKeyBase64) {
            throw new Error('AES key not found for the file.');
          }

          // Convert base64 back to array buffer
          const keyData = base64ToArrayBuffer(storedAesKeyBase64);
          
          // Import the AES key
          aesKey = await window.crypto.subtle.importKey(
            'raw',
            keyData,
            { name: 'AES-CBC', length: 256 },
            true,
            ['decrypt']
          );
          
          // Store it back in state
          setAesKeys(prev => ({ ...prev, [fileId]: aesKey }));
        }

        // Step 4: Decrypt the file
        const decryptedBuffer = await decryptFile(response.data, aesKey);

        // Step 5: Create a Blob and trigger download
        const decryptedBlob = new Blob([decryptedBuffer], { type: 'application/octet-stream' });

        // Get the original file name from the files state
        const fileObj = files.find(f => f.id === fileId);
        const originalName = fileObj?.originalFileName || fileName;
        
        // Get the extension from the converted file name
        const convertedExtension = fileName.slice(fileName.lastIndexOf('.'));
        
        // Add "_converted" to the original name but use the new extension
        const nameWithoutExt = originalName.slice(0, originalName.lastIndexOf('.'));
        const downloadName = `${nameWithoutExt}_converted${convertedExtension}`;

        // Use the modified name for download
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(decryptedBlob);
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        log('File download completed');

        // Cleanup: Revoke object URL and remove AES key from state
        window.URL.revokeObjectURL(link.href);
        setAesKeys(prev => {
          const updatedKeys = { ...prev };
          delete updatedKeys[fileId];
          return updatedKeys;
        });

        log('Download completed, initiating cleanup');
        
        const finalizationResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/conversion/finalizeToken`,
          { token },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        log('Finalization response:', finalizationResponse.data);

        if (finalizationResponse.data.success) {
          log('Cleanup successful');
          setFiles(prevFiles =>
            prevFiles.map(file =>
              file.id === fileId ? { ...file, status: 'deleted', displayName: downloadName } : file
            )
          );
          setFileStatuses(prev => ({ ...prev, [fileId]: 'Deleted', displayName: downloadName }));
          showToast.success(
            `${downloadName} downloaded successfully!`,
            'File has been cleaned up from our servers.'
          );
        } else {
          warn('Cleanup warning:', finalizationResponse.data);
          showToast.warning(
            'File downloaded with warnings',
            'The file was downloaded but cleanup may have been incomplete.'
          );
        }

        // Clear downloading state on success
        setDownloadingFiles(prev => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        
      } else {
        error('Token verification failed');
        showToast.error(`Error downloading ${fileName}: Invalid token`);
      }
    } catch (errorObj) {
      error('Download/cleanup error:', errorObj);
      
      // Clear downloading state on error
      setDownloadingFiles(prev => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });

      // Still allow the download to be considered successful
      if (errorObj.message.includes('cleanup')) {
        showToast.warning(
          'File downloaded with warnings',
          'The file was downloaded but server cleanup failed.'
        );
      } else {
        showToast.error(
          `Error downloading ${fileName}`,
          errorObj.message
        );
      }
    }
  };

  /**
   * Decrypt the downloaded file using AES-256-CBC
   */
  const decryptFile = async (encryptedData, aesKey) => {
    const encryptedArrayBuffer = encryptedData;
    // Extract IV from the first 16 bytes
    const iv = new Uint8Array(encryptedArrayBuffer.slice(0, 16));
    const data = encryptedArrayBuffer.slice(16);

    const decryptedContent = await window.crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv,
      },
      aesKey,
      data
    );
    log("File has been decrypted");
    return decryptedContent; // ArrayBuffer
  };


  const handleRemoveFile = (id) => {
    setFiles(files.filter(file => file.id !== id));
    setSelectedFiles(selectedFiles.filter(fileId => fileId !== id));
  };

  const handleSelectFile = (id) => {
    setSelectedFiles(prev =>
      prev.includes(id) ? prev.filter(fileId => fileId !== id) : [...prev, id]
    );
  };

  const handleBatchAction = async (action) => {
    switch (action) {
      case 'delete':
        setFiles(files.filter(file => !selectedFiles.includes(file.id)));
        setSelectedFiles([]);
        break;
      case 'convert':
        const filesToConvert = files.filter(file => selectedFiles.includes(file.id) && file.conversionType);
        if (filesToConvert.length === 0) {
          showToast.warning('No files selected for conversion or conversion type not set.');
          return;
        }

        setIsBatchConverting(true);
        setOverallProgress(0);

        for (let i = 0; i < filesToConvert.length; i++) {
          await handleUpload(filesToConvert[i]);
          setOverallProgress(((i + 1) / filesToConvert.length) * 100);
        }

        setIsBatchConverting(false);
        setOverallProgress(100);
        setSelectedFiles([]);
        showToast.success('Batch conversion completed.');
        break;
      default:
        break;
    }
  };


  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(files);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFiles(items);
  };

  useEffect(() => {
    const fetchPublicKey = async () => {
      try {
        log('Attempting to fetch public key...');
        const url = `${process.env.REACT_APP_API_URL}/api/keys/publicKey`;
        log('Fetching from URL:', url);
        
        const response = await axios.get(url, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true'
          },
          timeout: 5000
        });
        
        if (!response.data?.publicKey) {
          throw new Error('Invalid public key response');
        }
        
        setPublicKey(response.data.publicKey);
        log('Public key fetched successfully');
        
      } catch (errorObj) {
        error('Error fetching public key:', errorObj);
        showToast.error('Failed to fetch encryption key. Please try again.');
      }
    };
    fetchPublicKey();
  }, []);

  // Has been tested and works. 
  const generateAndEncryptAesKey = async () => {
    if (!publicKey) {
      error('Public key is not available');
      throw new Error('Encryption key not available. Please try again.');
    }
    
    try {
      // Generate AES-256 key
      const aesKey = await window.crypto.subtle.generateKey(
        {
          name: 'AES-CBC',
          length: 256,
        },
        true,
        ['encrypt', 'decrypt']
      );
      
      log('AES key generated successfully');
      
      // Export the raw AES key
      const rawAesKey = await window.crypto.subtle.exportKey('raw', aesKey);
      log('AES key exported successfully');
      
      // Import the RSA public key
      log('Attempting to import public key:', publicKey.substring(0, 50) + '...');
      const rsaPublicKey = await importPublicKey(publicKey);
      log('RSA public key imported successfully');
      
      // Encrypt the AES key using RSA-OAEP
      const encryptedAesKey = await window.crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP',
        },
        rsaPublicKey,
        rawAesKey
      );
      
      // Convert encrypted AES key to Base64
      const encryptedAesKeyBase64 = arrayBufferToBase64(encryptedAesKey);
      
      return { aesKey, encryptedAesKey: encryptedAesKeyBase64 };
    } catch (errorObj) {
      error('Error generating and encrypting AES key:', errorObj);
      throw new Error('Failed to generate and encrypt AES key: ' + errorObj.message);
    }
  };
  
  // Utility functions
  const importPublicKey = async (pem) => {
    try {
      log('Importing public key, format check:', {
        isPEM: pem.includes('-----BEGIN PUBLIC KEY-----'),
        length: pem.length
      });
      
      if (!pem || typeof pem !== 'string') {
        throw new Error('Invalid public key format');
      }
      
      const pemContent = pem.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----/g, '');
      const binaryDerString = window.atob(pemContent.trim());
      const binaryDer = str2ab(binaryDerString);
      
      return await window.crypto.subtle.importKey(
        'spki',
        binaryDer,
        {
          name: 'RSA-OAEP',
          hash: 'SHA-256',
        },
        true,
        ['encrypt']
      );
    } catch (errorObj) {
      error('Error importing public key:', errorObj);
      throw new Error('Failed to import public key: ' + errorObj.message);
    }
  };
  
  const str2ab = (str) => {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }
    return buf;
  };
  
  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const getThemeColors = (selectedConversion, currentTheme) => {
    const defaultTheme = {
      border: 'border-primary-400',
      background: 'bg-primary-50/10'
    };

    if (selectedConversion === 'default') {
      return defaultTheme;
    }

    return {
      border: currentTheme?.dragActive?.border || defaultTheme.border,
      background: currentTheme?.dragActive?.background || defaultTheme.background
    };
  };

  const getDropAreaClasses = () => {
    const baseClasses = [
      "relative",
      "flex flex-col",
      "items-center justify-center",
      "w-full h-72",
      "p-8",
      darkMode ? "bg-gray-900/90" : "bg-white",
      "rounded-xl",
      "cursor-pointer",
      "transition-all duration-300 ease-out",
      "border-2",
      darkMode ? "border-gray-800" : "",
      "shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)]",
      "hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)]",
      "hover:-translate-y-2",
      "hover:scale-[1.01]",
      darkMode ? "bg-gradient-to-b from-gray-900/90 to-gray-950/80" : "bg-gradient-to-b from-white to-gray-50/50",
      "active:scale-[0.99]",
      "active:shadow-[0_10px_40px_-12px_rgba(0,0,0,0.12)]",
      "active:translate-y-0",
    ].join(' ');

    const theme = getThemeColors(selectedConversion, currentTheme);
    
    const borderClasses = isDragActive
      ? `border-solid ${theme.border} ${theme.background}`
      : `border-dashed border-gray-200 hover:border-solid hover:${theme.border}`;

    return `${baseClasses} ${borderClasses}`;
  };

  return (
    <>
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} shadow-md w-full max-w-4xl mx-auto mt-4 transition-colors duration-300`}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <ActionButton
              icon={<FiList className="w-4 h-4" />}
              onClick={() => setIsModalOpen(true)}
              variant="default"
            >
              View Conversions
            </ActionButton>
            
            <ActionButton
              icon={<FiClock className="w-4 h-4" />}
              onClick={() => setShowHistory(true)}
              variant="ghost"
            >
              History
            </ActionButton>
          </div>

          <ActionButton
            icon={darkMode ? <FiSun className="w-4 h-4" /> : <FiMoon className="w-4 h-4" />}
            onClick={toggleDarkMode}
            variant="ghost"
          >
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </ActionButton>
        </div>
    
        <motion.div
          {...getRootProps()}
          className={getDropAreaClasses()}
          animate={{
            scale: isDragActive ? 1.02 : 1,
            y: isDragActive ? -8 : 0,
          }}
          whileHover={{
            y: -4,
            transition: { duration: 0.2 }
          }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20
          }}
        >
          <input {...getInputProps()} />
          
          {/* Add a subtle gradient background effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50/30 to-gray-100/50 
                    opacity-0 group-hover:opacity-100 transition-all duration-300" />
          
          {/* Add a subtle border glow effect */}
          <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
                    transition-opacity duration-300 pointer-events-none
                    bg-gradient-to-r from-primary-200/0 via-primary-200/10 to-primary-200/0
                    blur-xl" />

          {/* Keep your original icon and text container unchanged */}
          <motion.div
            className="relative z-10 flex flex-col items-center gap-4"
            animate={{ 
              y: isDragActive ? -8 : 0,
              scale: isDragActive ? 1.1 : 1
            }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 20 
            }}
          >
            {/* Original icon with unchanged styling */}
            <FiUploadCloud 
              className={`h-16 w-16 transition-all duration-300
                ${selectedConversion === 'default'
                  ? 'text-primary-400'
                  : isDragActive 
                    ? currentTheme?.dragActive?.text || 'text-primary-500'
                    : currentTheme?.icon || 'text-gray-400'
                }
                drop-shadow-md hover:drop-shadow-xl
                ${darkMode ? 'drop-shadow-[0_2px_16px_rgba(16,185,129,0.15)]' : ''}`}
            />
            
            {/* Original text styling */}
            <p className={`
              text-lg text-center font-medium
              transition-all duration-300
              ${isDragActive 
                ? selectedConversion === 'default'
                  ? 'text-primary-600'
                  : currentTheme.dragActive.text
                : darkMode ? 'text-neutral-100' : 'text-gray-600'
              }
              drop-shadow-md
              ${darkMode ? 'drop-shadow-[0_2px_8px_rgba(16,185,129,0.10)]' : ''}`}
            >
              {isDragActive ? "Drop the files here" : "Drag & drop files here, or click to select"}
            </p>
          </motion.div>
          {/* Dark mode overlay for extra contrast, does not override conversion-specific effects */}
          {darkMode && (
            <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-gray-900/80 via-gray-950/60 to-gray-900/90 opacity-80 z-0" />
          )}

          {/* Add subtle animated particles for drag state */}
          {isDragActive && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Add some decorative dots */}
              <div className="absolute inset-0">
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 rounded-full bg-primary-400/30"
                    animate={{
                      y: [-20, 20],
                      x: Math.sin(i) * 10,
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: i * 0.2,
                    }}
                    style={{
                      left: `${20 + i * 15}%`,
                      top: `${30 + (i % 3) * 20}%`,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
    
        {files.length > 0 && (
          <div className="uploaded-files-section mt-6 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Files to Convert</h3>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleBatchAction('delete')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200
                    ${selectedFiles.length === 0
                      ? 'bg-rose-50 text-rose-500 hover:bg-rose-100'
                      : 'bg-rose-500 text-white hover:bg-rose-600'
                    }
                    disabled:opacity-60 disabled:cursor-not-allowed
                    shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                    hover:shadow-[0_2px_4px_rgba(0,0,0,0.05)]
                    active:transform active:scale-[0.98]`}
                  disabled={isBatchConverting}
                >
                  <div className="flex items-center space-x-2">
                    <FiTrash2 className="w-4 h-4" />
                    <span>Delete Selected</span>
                  </div>
                </button>
                
                <button
                  onClick={() => handleBatchAction('convert')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200
                    relative overflow-hidden
                    ${selectedFiles.length === 0
                      ? 'bg-blue-50 text-blue-500 hover:bg-blue-100'
                      : isBatchConverting
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }
                    disabled:opacity-60 disabled:cursor-not-allowed
                    shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                    hover:shadow-[0_2px_4px_rgba(0,0,0,0.05)]
                    active:transform active:scale-[0.98]`}
                  disabled={isBatchConverting}
                >
                  <div className="flex items-center space-x-2 relative z-10">
                    {isBatchConverting ? (
                      <>
                        <FiLoader className="w-4 h-4 animate-spin" />
                        <span>Converting...</span>
                      </>
                    ) : (
                      <>
                        <FiCheck className="w-4 h-4" />
                        <span>Convert Selected</span>
                      </>
                    )}
                  </div>
                  {isBatchConverting && (
                    <div
                      className="absolute left-0 top-0 h-full bg-blue-600 transition-all duration-300 ease-in-out"
                      style={{ width: `${overallProgress}%` }}
                    />
                  )}
                </button>
              </div>
            </div>
    
            <div className="overflow-x-auto">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="fileList">
                  {(provided) => (
                    <ul {...provided.droppableProps} ref={provided.innerRef} className="space-y-2 min-w-max">
                      {files.map((fileObj, index) => (
                        <Draggable key={fileObj.id} draggableId={fileObj.id.toString()} index={index}>
                          {(provided) => (
                            <motion.li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200
                                ${darkMode 
                                  ? 'bg-gradient-to-r from-gray-800/90 to-gray-800/50 backdrop-blur-sm border border-gray-700/50 hover:from-gray-800/95 hover:to-gray-800/60' 
                                  : 'bg-gradient-to-r from-white/95 to-white/80 backdrop-blur-sm border border-gray-200/50 hover:from-white hover:to-white/90'
                                }
                                shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]`}
                            >
                              <div className="flex items-center space-x-3 w-1/3 min-w-[200px] max-w-[300px] flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={selectedFiles.includes(fileObj.id)}
                                  onChange={() => handleSelectFile(fileObj.id)}
                                  className="form-checkbox h-4 w-4 text-primary-600 transition duration-150 ease-in-out
                                    border-gray-300 rounded focus:ring-primary-500"
                                />
                                <div className="flex-shrink-0 p-1.5 rounded-md 
                                  bg-gradient-to-br from-white/90 to-white/50 
                                  shadow-[0_2px_6px_-2px_rgba(0,0,0,0.05)]
                                  border border-white/20">
                                  {getFileIcon(fileObj.file.name)}
                                </div>
                                <span 
                                  className="font-medium truncate flex-grow text-sm"
                                  title={fileObj.file.name}
                                >
                                  {fileObj.file.name}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 flex-grow justify-end">
                                <FileStatusIndicator
                                  status={fileStatuses[fileObj.id] || 'Uploaded'}
                                  progress={conversionProgress[fileObj.id]}
                                />
                                <select
                                  value={fileObj.conversionType}
                                  onChange={(e) => handleFileConversionChange(fileObj.id, e.target.value)}
                                  className={`form-select block w-40 px-3 py-1.5 text-sm rounded-md border-0
                                    transition-all duration-200
                                    ${darkMode 
                                      ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white hover:from-gray-600 hover:to-gray-700 focus:ring-2 focus:ring-primary-500' 
                                      : 'bg-gradient-to-r from-white to-gray-50 text-gray-900 hover:from-gray-50 hover:to-gray-100 focus:ring-2 focus:ring-primary-500'
                                    }
                                    shadow-[0_2px_8px_-3px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_12px_-3px_rgba(0,0,0,0.15)]
                                    focus:outline-none`}
                                >
                                  <option value="" className={darkMode ? 'bg-gray-700' : 'bg-white'}>
                                    Select conversion
                                  </option>
                                  {getConversionOptions(fileObj.file.name).map(option => (
                                    <option 
                                      key={option.value} 
                                      value={option.value} 
                                      className={darkMode ? 'bg-gray-700' : 'bg-white'}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                <div className="flex space-x-2 flex-shrink-0">
                                  {fileObj.converted ? (
                                    <button
                                      onClick={() => fileObj.status !== 'deleted' ? handleDownload(fileObj.convertedUrl, fileObj.convertedFileName, fileObj.token, fileObj.id) : null}
                                      className={`p-1.5 w-8 h-7 flex items-center justify-center rounded-md transition-all duration-200
                                        ${fileObj.status === 'deleted' 
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                          : downloadingFiles[fileObj.id]
                                            ? 'bg-emerald-500/80 text-white'
                                            : 'bg-emerald-500 text-white hover:bg-emerald-600'
                                        }
                                        shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                                        hover:shadow-[0_2px_4px_rgba(0,0,0,0.05)]
                                        active:transform active:scale-[0.98]
                                        disabled:opacity-75`}
                                      disabled={fileObj.status === 'deleted' || downloadingFiles[fileObj.id]}
                                    >
                                      <div className="w-4 h-4">
                                        {fileObj.status === 'deleted' ? (
                                          <FiCheck className="w-4 h-4" />
                                        ) : downloadingFiles[fileObj.id] ? (
                                          <FiLoader className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <FiDownload className="w-4 h-4" />
                                        )}
                                      </div>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleUpload(fileObj)}
                                      disabled={!fileObj.conversionType || conversionInProgress[fileObj.id] || fileObj.converted}
                                      className={`p-1.5 w-8 h-7 flex items-center justify-center rounded-md transition-all duration-200
                                        ${fileObj.conversionType && !conversionInProgress[fileObj.id] && !fileObj.converted
                                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }
                                        shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                                        hover:shadow-[0_2px_4px_rgba(0,0,0,0.05)]
                                        active:transform active:scale-[0.98]`}
                                    >
                                      <div className="w-4 h-4">
                                        {conversionInProgress[fileObj.id] ? (
                                          <FiLoader className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <FiCheck className="w-4 h-4" />
                                        )}
                                      </div>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleRemoveFile(fileObj.id)}
                                    className="p-1.5 w-8 h-7 flex items-center justify-center rounded-md transition-all duration-200
                                      bg-rose-500 text-white hover:bg-rose-600
                                      shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                                      hover:shadow-[0_2px_4px_rgba(0,0,0,0.05)]
                                      active:transform active:scale-[0.98]"
                                  >
                                    <div className="w-4 h-4">
                                      <FiTrash2 className="w-4 h-4" />
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </motion.li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        )}
    
        <SlideOutPanel isOpen={showHistory} onClose={() => setShowHistory(false)} darkMode={darkMode}>
          <ConversionHistory
            history={conversionHistory}
            darkMode={darkMode}
            clearHistory={clearConversionHistory}
          />
        </SlideOutPanel>
    
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable={false}
          pauseOnHover
          theme={darkMode ? 'dark' : 'light'}
          className="!-translate-y-4"
          limit={5}
          closeButton={false}
        />
    
        <ConversionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          conversionOptions={conversionOptions}
          darkMode={darkMode}
        />
        <Tooltip id="status-tooltip" />
      </div>
    </>
  );
};

export default FileUpload;

